<template>
  <v-card class="rounded-lg fill-height">
    <v-card-title class="pa-4">
      <v-row
        align="center"
        no-gutters
      >
        <v-col>
          <div class="subtitle-2 grey--text text--grey-100">
            {{ $t('player_insights.table.title') }}
          </div>
        </v-col>
        <v-col
          cols="auto"
          class="pl-2"
        >
          <variable-selector
            v-if="expandedVariables.length > 0"
            :variables="expandedVariables"
            :layout-variables.sync="selectedVariablesList"
            :excluded-variables="[]"
            :use-long-text="true"
            @update="$emit('variables-updated')"
          />
        </v-col>
      </v-row>
    </v-card-title>
    <v-card-text class="pa-0">
      <v-data-table
        :headers="tableHeaders"
        :items="playerStats"
        :loading="loading"
        hide-default-footer
        disable-pagination
        :items-per-page="-1"
        sort-by="player.last_name"
        dense
      >
        <template v-slot:item="{ item }">
          <tr
            style="cursor: pointer;"
            @click="$emit('player-click', item)"
          >
            <td
              style="width: 0px"
              class="pr-0"
            >
              <v-avatar
                size="32px"
                class="mr-2"
                color="grey lighten-2"
              >
                <v-img :src="item.player.photo_url">
                  <template v-slot:placeholder>
                    <v-icon x-small>
                      fal fa-user
                    </v-icon>
                  </template>
                </v-img>
              </v-avatar>
            </td>
            <td>
              <div class="d-flex align-center">
                <div class="flex-grow-1">
                  {{ item.player.last_name }} {{ item.player.first_name }}
                </div>
                <div
                  v-if="item.player.latest_stats && item.player.latest_stats.injured"
                  class="d-flex align-center justify-center ml-2"
                  style="width: 24px; min-width: 24px;"
                >
                  <img
                    :src="injuryIcon"
                    style="width: 22px; height: 22px"
                  >
                </div>
                <div
                  v-if="hasSmartAssistantModule"
                  class="d-flex align-center justify-center ml-2"
                  style="width: 24px; min-width: 24px; cursor: pointer;"
                  @click.stop.prevent="$router.push({name: $routeNames.PLAYER_SMART_ASSISTANT, params: {id: item.player_id}})"
                >
                  <v-tooltip
                    v-if="item.player.smart_assistant && item.player.smart_assistant.length"
                    top
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <span
                        v-bind="attrs"
                        v-on="on"
                      >
                        <v-badge
                          :content="item.player.smart_assistant.length"
                          color="primary"
                          inline
                          :value="item.player.smart_assistant.length"
                        />
                      </span>
                    </template>
                    <span>{{ $tc('tooltip.smart_assistant', item.player.smart_assistant.length, { num: item.player.smart_assistant.length }) }}</span>
                  </v-tooltip>
                  <v-tooltip
                    v-else
                    top
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <span
                        v-bind="attrs"
                        v-on="on"
                      >
                        <v-icon
                          color="success"
                          size="22px"
                        >
                          fas fa-circle-check
                        </v-icon>
                      </span>
                    </template>
                    <span>{{ $t('smart_assistant.no_alerts') }}</span>
                  </v-tooltip>
                </div>
              </div>
            </td>
            <td>
              <v-chip
                v-for="position in (item.player.player_positions || []).slice(0, 3)"
                :key="item.player_id + '-' + position.id"
                small
                label
                class="mx-1"
              >
                {{ position.short_name }}
              </v-chip>
            </td>
            <td
              v-for="variable in selectedVariables"
              :key="variableStatsKey(variable)"
              :style="isTagBreakdownVar(variable) ? 'min-width: 180px' : 'min-width: 100px'"
            >
              <template v-if="isTagBreakdownVar(variable)">
                <tag-breakdown-cell
                  :stats="item.stats"
                  :tag="getTagForVariable(variable)"
                  :stat-prefix="variableStatsPrefix(variable)"
                  :suffix="variable.suffix"
                />
              </template>
              <template v-else>
                <span
                  class="d-block"
                  :class="{ 'grey--text text--lighten-1': item.stats[variableStatsKey(variable)] == null }"
                >
                  {{ formatStatValue(item.stats[variableStatsKey(variable)], variable) }}
                </span>
              </template>
            </td>
          </tr>
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>

<script>
import TagBreakdownCell from '@/components/coach-insights/TagBreakdownCell.vue'
import VariableSelector from '@/components/performance/VariableSelector.vue'
import injuryIcon from '@/assets/img/player_info/Injury indicator.svg'

export default {
  name: 'PlayerInsightsTable',
  components: { TagBreakdownCell, VariableSelector },
  props: {
    playerStats: {
      type: Array,
      required: true
    },
    selectedVariables: {
      type: Array,
      default: () => []
    },
    variables: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    injuryIcon
  }),
  computed: {
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    },
    hasSmartAssistantModule() {
      return this.user?.organisation_settings?.has_smart_assistant_module
    },
    expandedVariables() {
      const result = []
      this.variables.forEach(v => {
        result.push(v)
        if (v.suffix_agg && typeof v.suffix_agg === 'object') {
          Object.keys(v.suffix_agg).forEach(aggKey => {
            result.push({ ...v, aggregation: aggKey })
          })
        }
      })
      return result
    },
    selectedVariablesList: {
      get() {
        return this.selectedVariables
      },
      set(value) {
        this.$emit('update:selectedVariables', value)
      }
    },
    tableHeaders() {
      const headers = [
        { text: '', value: 'avatar', sortable: false, width: '0px' },
        { text: this.$t('player_insights.table.player'), value: 'player.last_name', sortable: true, width: '250px' },
        { text: this.$t('table_headers.player_overview.position'), value: 'player_positions', sortable: false, width: '150px' }
      ]

      this.selectedVariables.forEach(variable => {
        let text = variable.long_text || variable.text || variable.value
        if (variable.aggregation) {
          const aggLabel = this.getAggregationLabel(variable.aggregation)
          text += ` (${aggLabel})`
        }
        headers.push({
          text,
          value: this.variableStatsKey(variable),
          sortable: false,
          width: this.isTagBreakdownVar(variable) ? '180px' : '120px'
        })
      })

      return headers
    }
  },
  methods: {
    variableStatsKey(variable) {
      const varKey = variable.aggregation ? `${variable.value}_${variable.aggregation}` : variable.value
      return `${variable.category.toLowerCase()}.${varKey}`
    },
    variableStatsPrefix(variable) {
      return `${variable.category.toLowerCase()}.${variable.value}`
    },
    isTagBreakdownVar(variable) {
      return !!variable.aggregation
    },
    getTagForVariable(variable) {
      return {
        id: variable.aggregation,
        label: this.getAggregationLabel(variable.aggregation)
      }
    },
    getAggregationLabel(aggregation) {
      if (aggregation === 'group_id') return this.$t('insights.by_group')
      return aggregation.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    },
    formatStatValue(value, variable) {
      if (value == null) return 'n/a'

      if (typeof value === 'object') {
        if (typeof value.total === 'number') {
          const formatted = Number.isInteger(value.total) ? value.total : Math.round(value.total * 100) / 100
          return variable.suffix ? `${formatted} ${variable.suffix}` : formatted
        }
        return 'n/a'
      }

      if (typeof value === 'number') {
        const formatted = Number.isInteger(value) ? value : Math.round(value * 100) / 100
        return variable.suffix ? `${formatted} ${variable.suffix}` : formatted
      }

      return value
    }
  }
}
</script>
